import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import AuthShell from '../auth/AuthShell';
import { underlineStyle, primaryButtonSx, errorBoxSx } from '../auth/authStyles';

function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : name.slice(0, 2)).toUpperCase();
}

export default function PortalLoginPage() {
    const navigate = useNavigate();
    const { login, switchCompany } = usePortalAuth();

    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    // Set when the account can act for several companies → picker step.
    const [choices, setChoices] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
        if (error) setError(null);
    };

    const handleLogin = async () => {
        if (!credentials.email || !credentials.password) {
            setError('Please enter your email and password.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await login(credentials);
            if ((data.memberships || []).length > 1) {
                setChoices(data.memberships);
            } else {
                navigate('/portal/quotes');
            }
        } catch {
            setError('Invalid email or password.');
        } finally {
            setLoading(false);
        }
    };

    const handlePick = async (membership) => {
        setLoading(true);
        setError(null);
        try {
            await switchCompany(membership.customerId);
            navigate('/portal/quotes');
        } catch {
            setError('Could not open this company. Please try again.');
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleLogin();
    };

    if (choices) {
        return (
            <AuthShell centered>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#427898', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, mb: 1, fontSize: 11 }}
                    >
                        Customer Portal
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>
                        Choose a company
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c9a' }}>
                        Your account has access to several portals
                    </Typography>
                </Box>

                {error && (
                    <Box sx={errorBoxSx}>
                        <Typography variant="body2" color="error">{error}</Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, opacity: loading ? 0.6 : 1 }}>
                    {choices.map((m) => (
                        <Box
                            key={m.customerId}
                            onClick={() => !loading && handlePick(m)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: 1.5, borderRadius: '12px', cursor: loading ? 'default' : 'pointer',
                                border: '1px solid #e6ecf1', backgroundColor: '#fff',
                                transition: 'border-color .15s, box-shadow .15s',
                                '&:hover': loading ? {} : { borderColor: '#427898', boxShadow: '0 2px 10px rgba(66,120,152,0.12)' },
                            }}
                        >
                            {m.clientLogoBase64 ? (
                                <Box
                                    component="img"
                                    src={`data:image/png;base64,${m.clientLogoBase64}`}
                                    alt={m.clientName}
                                    sx={{ height: 34, width: 34, objectFit: 'contain', flexShrink: 0 }}
                                />
                            ) : (
                                <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #427898, #1e3a52)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                                    {initials(m.clientName)}
                                </Box>
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#17262f' }} noWrap>
                                    {m.clientName}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#7f8c9a' }} noWrap>
                                    {m.customerName}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 18, color: '#94a3b8', lineHeight: 1 }}>›</Typography>
                        </Box>
                    ))}
                </Box>
            </AuthShell>
        );
    }

    return (
        <AuthShell centered>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        color: '#427898',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        mb: 1,
                        fontSize: 11,
                    }}
                >
                    Customer Portal
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>
                    Sign in
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a' }}>
                    Access your documents and account
                </Typography>
            </Box>

            {/* Error */}
            {error && (
                <Box sx={errorBoxSx}>
                    <Typography variant="body2" color="error">{error}</Typography>
                </Box>
            )}

            {/* Email */}
            <TextField
                fullWidth
                variant="standard"
                label="Email"
                name="email"
                type="email"
                value={credentials.email}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                sx={{ mb: 3, ...underlineStyle }}
            />

            {/* Password */}
            <TextField
                fullWidth
                variant="standard"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={() => setShowPassword(v => !v)}
                                edge="end"
                                tabIndex={-1}
                            >
                                {showPassword
                                    ? <VisibilityOff fontSize="small" />
                                    : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 4, ...underlineStyle }}
            />

            {/* Sign in button */}
            <Button
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleLogin}
                sx={primaryButtonSx}
            >
                {loading
                    ? <CircularProgress size={22} sx={{ color: 'white' }} />
                    : 'Sign in'}
            </Button>

            {/* Help text */}
            <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 3, color: '#94a3b8', fontSize: 13 }}
            >
                Need help? Contact your service provider.
            </Typography>
        </AuthShell>
    );
}
